import { Link } from 'react-router-dom';
import './Collaborations.css';
import { FaHandshake, FaProjectDiagram, FaChartLine, FaFileContract, FaBalanceScale, FaUsers, FaLandmark, FaAward } from 'react-icons/fa';

export default function Collaborations() {
  const services = [
    {
      icon: <FaHandshake />,
      title: 'Joint Ventures',
      description: 'Strategic partnerships for land owners and developers to maximize property value and returns.'
    },
    {
      icon: <FaProjectDiagram />,
      title: 'Development Partnerships',
      description: 'Collaborative development projects combining resources, expertise, and market knowledge.'
    },
    {
      icon: <FaLandmark />,
      title: 'Land Pooling',
      description: 'Consolidate multiple land parcels for large-scale development projects and better returns.'
    },
    {
      icon: <FaFileContract />,
      title: 'Revenue Sharing',
      description: 'Transparent revenue sharing models with clear terms and regular financial reporting.'
    },
    {
      icon: <FaBalanceScale />,
      title: 'Legal Framework',
      description: 'Comprehensive legal agreements protecting interests of all parties involved.'
    },
    {
      icon: <FaChartLine />,
      title: 'Project Management',
      description: 'End-to-end project coordination from planning to completion and handover.'
    },
    {
      icon: <FaUsers />,
      title: 'Partner Matching',
      description: 'Connect with verified developers, investors, and land owners for mutual growth.'
    },
    {
      icon: <FaAward />,
      title: 'Quality Assurance',
      description: 'Ensuring project quality standards and timely delivery with regular monitoring.'
    }
  ];

  const collaborationTypes = [
    {
      title: 'Land Owner & Developer',
      description: 'Land owners contribute property while developers bring expertise and capital',
      benefits: ['No upfront investment from land owner', 'Share in developed property value', 'Professional project execution']
    },
    {
      title: 'Developer & Investor',
      description: 'Developers provide expertise while investors contribute capital funding',
      benefits: ['Access to quality projects', 'Transparent financial reporting', 'Profitable returns on investment']
    },
    {
      title: 'Multiple Stakeholders',
      description: 'Complex collaborations involving multiple parties with different contributions',
      benefits: ['Large-scale project capability', 'Risk distribution among partners', 'Combined expertise and resources']
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Partnership Structuring',
      description: 'Define collaboration model, roles, and contribution of each party.'
    },
    {
      number: '02',
      title: 'Legal Documentation',
      description: 'Draft comprehensive agreements covering all aspects of the partnership.'
    },
    {
      number: '03',
      title: 'Project Execution',
      description: 'Implement development plan with regular monitoring and reporting.'
    },
    {
      number: '04',
      title: 'Revenue Distribution',
      description: 'Fair distribution of returns as per agreed terms and timelines.'
    }
  ];

  return (
    <>
      <section className="collab-hero">
        <div className="collab-hero-content">
          <h1>Property Collaborations</h1>
          <p>Strategic partnerships for successful real estate ventures</p>
        </div>
      </section>

      <section className="collab-intro">
        <div className="collab-container">
          <div className="intro-content">
            <h2>Building Successful Real Estate Partnerships</h2>
            <p>
              We facilitate strategic collaborations between land owners, developers, and investors to create 
              mutually beneficial real estate projects. Our expertise in structuring joint ventures and partnership 
              agreements ensures transparent, profitable, and legally sound collaborations for all parties involved.
            </p>
          </div>
        </div>
      </section>

      <section className="services-section">
        <div className="collab-container">
          <h2 className="section-title">Our Collaboration Services</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="collab-types-section">
        <div className="collab-container">
          <h2 className="section-title">Types of Collaborations</h2>
          <div className="collab-types-grid">
            {collaborationTypes.map((type, index) => (
              <div key={index} className="collab-type-card" style={{ animationDelay: `${index * 0.15}s` }}>
                <h3>{type.title}</h3>
                <p className="type-description">{type.description}</p>
                <ul className="benefits-list">
                  {type.benefits.map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section">
        <div className="collab-container">
          <h2 className="section-title">Our Process</h2>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="benefits-section">
        <div className="collab-container">
          <div className="benefits-wrapper">
            <div className="benefits-image">
              <img 
                src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Business Partnership" 
              />
            </div>
            <div className="benefits-content">
              <h2>Why Choose Our Collaboration Services?</h2>
              <ul className="key-benefits-list">
                <li>20+ years of experience in structuring JV agreements</li>
                <li>Transparent and fair revenue sharing models</li>
                <li>Complete legal and regulatory compliance</li>
                <li>Regular project updates and financial reporting</li>
                <li>Proven track record of successful partnerships</li>
                <li>End-to-end project management support</li>
                <li>Conflict resolution and mediation services</li>
                <li>Network of verified partners and investors</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="collab-cta">
        <div className="collab-container">
          <div className="cta-content">
            <h2>Ready to Explore Partnership Opportunities?</h2>
            <p>Connect with our collaboration experts for a free consultation</p>
            <Link to="/contact" className="cta-button">
              Schedule a Meeting
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
